import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Image,
  Input,
  List,
  message,
  Modal,
  Row,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { orderAPI, productAPI, dropshipperAPI } from '../api';
import { BRAND_PRIMARY } from '../utils/brandColors';
import { normalizeMoney } from '../utils/money';
import countryList from 'react-select-country-list';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const { TextArea } = Input;
const { Text } = Typography;

const humanizeKey = (key) => key
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const statusColorMap = {
  AWAITING_PAYMENT: BRAND_PRIMARY,
  CONFIRMED: BRAND_PRIMARY,
  CANCELLED: 'red',
  CLOSED: 'green',
};

const statusTextMap = {
  AWAITING_PAYMENT: 'Awaiting Payment',
  CONFIRMED: 'Paid',
  CANCELLED: 'Cancelled',
  CLOSED: 'Completed',
};

const fulfillmentColorMap = {
  AWAITING: BRAND_PRIMARY,
  ACCEPTED: BRAND_PRIMARY,
  REJECTED: 'red',
  ON_HOLD: 'yellow',
  SHIPPED: 'green',
  DELIVERED: 'cyan',
};

const fulfillmentTextMap = {
  AWAITING: 'Awaiting',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  ON_HOLD: 'On Hold',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
};

const rmaColorMap = {
  RMA_REQUESTED: BRAND_PRIMARY,
  RMA_AUTHORIZED: BRAND_PRIMARY,
  RMA_IN_TRANSIT: 'yellow',
  RMA_RECEIVED: 'green',
  REFUND_COMPLETED: 'cyan',
};

const rmaTextMap = {
  RMA_REQUESTED: 'RMA Requested',
  RMA_AUTHORIZED: 'RMA Authorized',
  RMA_IN_TRANSIT: 'In Transit',
  RMA_RECEIVED: 'Received',
  REFUND_COMPLETED: 'Refund Completed',
};

const getStatusColor = (status) => statusColorMap[status] || 'default';
const getStatusText = (status) => statusTextMap[status] || status;
const getFulfillmentStatusColor = (status) => fulfillmentColorMap[status] || 'default';
const getFulfillmentStatusText = (status) => fulfillmentTextMap[status] || status;
const getRMAStatusColor = (status) => rmaColorMap[status] || 'default';
const getRMAStatusText = (status) => rmaTextMap[status] || status;

const getVariantInfo = (variants, variantPath) => {
  if (!variants || !Array.isArray(variants)) {
    return null;
  }

  if (!variantPath || variantPath.length === 0) {
    for (const variant of variants) {
      if (!variant.variant_path || variant.variant_path.length === 0) {
        return variant;
      }
    }
    return null;
  }

  return variants.find((variant) => {
    const path = variant.variant_path || [];
    return JSON.stringify(path) === JSON.stringify(variantPath);
  }) || null;
};

const getProductCoverImage = (product) => {
  if (!product?.images?.length) {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
  }

  const coverImage = product.images.find((img) => !img.for_variants) || product.images[0];
  return `${API_BASE_URL}/products/${product.id}/images/${coverImage.id}`;
};

const getVariantImage = (product, variantPath) => {
  if (!product?.images?.length || !variantPath || variantPath.length === 0) {
    return null;
  }

  const variantInfo = getVariantInfo(product.variants, variantPath);
  if (!variantInfo?.image) {
    return null;
  }

  return `${API_BASE_URL}/products/${product.id}/images/${variantInfo.image}`;
};

const OrderDetails = () => {
  const navigate = useNavigate();
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState({});
  const [messageModalVisible, setMessageModalVisible] = useState(false);
  const [messageForm] = Form.useForm();
  const [updatingMessage, setUpdatingMessage] = useState(false);
  const [freightChannels, setFreightChannels] = useState([]);
  const [customizations, setCustomizations] = useState([]);
  const countryListInstance = useMemo(() => countryList(), []);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await orderAPI.getByNumber(orderNumber);
        const data = response.data;
        const normalizedOrder = {
          ...data,
          total_amount: data.total_amount != null ? normalizeMoney(data.total_amount) : '0.00',
          price_details: (data.price_details || []).map((detail) => ({
            ...detail,
            amount: detail?.amount != null ? normalizeMoney(detail.amount) : '0.00',
          })),
          items_detail: (data.items_detail || []).map((item) => ({
            ...item,
            unit_price: item?.unit_price != null ? normalizeMoney(item.unit_price) : '0.00',
            customization_cost: item?.customization_cost != null ? normalizeMoney(item.customization_cost) : '0.00',
          })),
        };

        setOrder(normalizedOrder);

        const productIds = new Set();
        normalizedOrder.items_detail.forEach((item) => {
          if (item.product_id) {
            productIds.add(item.product_id);
          }
        });

        const productResults = await Promise.all(
          Array.from(productIds).map(async (productId) => {
            try {
              const productResponse = await productAPI.getById(productId, { include_variant_images: true });
              return { id: productId, data: productResponse.data };
            } catch (error) {
              
              return { id: productId, data: null };
            }
          })
        );

        const productMap = {};
        productResults.forEach((result) => {
          productMap[result.id] = result.data;
        });
        setProducts(productMap);
      } catch (error) {
        message.error('Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderNumber]);

  useEffect(() => {
    const fetchSupportingData = async () => {
      try {
        const response = await dropshipperAPI.getFreightChannels();
        setFreightChannels(response.data || []);
      } catch (error) {
        
      }

      try {
        const response = await dropshipperAPI.getMyCustomizations();
        setCustomizations(response.data || []);
      } catch (error) {
        
      }
    };

    fetchSupportingData();
  }, []);

  const freightChannelMap = useMemo(() => {
    const map = {};
    freightChannels.forEach((channel) => {
      map[channel.id] = channel;
    });
    return map;
  }, [freightChannels]);

  const customizationMap = useMemo(() => {
    const map = {};
    customizations.forEach((customization) => {
      map[customization.id] = customization;
    });
    return map;
  }, [customizations]);

  const formatCountry = useCallback((code, emptyText) => {
    if (!code) {
      return emptyText;
    }
    const normalized = (code || '').toUpperCase();
    const label = countryListInstance.getLabel(normalized) || '';
    return label ? `${normalized} (${label})` : normalized;
  }, [countryListInstance]);

  const handleProductClick = (productId) => {
    window.open(`/product/${productId}`, '_blank');
  };

  const handleEditMessage = () => {
    if (!order) {
      return;
    }
    messageForm.setFieldsValue({ message: order.message || '' });
    setMessageModalVisible(true);
  };

  const handleUpdateMessage = async (values) => {
    if (!order) {
      return;
    }
    try {
      setUpdatingMessage(true);
      await orderAPI.updateMessage(order.order_number, values.message);
      message.success('Order message updated successfully');
      setMessageModalVisible(false);
      setOrder((prev) => prev ? { ...prev, message: values.message } : prev);
    } catch (error) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('Failed to update order message');
      }
    } finally {
      setUpdatingMessage(false);
    }
  };

  const orderContent = useMemo(() => {
    if (!order) {
      return null;
    }

    return (
      <div>
        <Descriptions title="Order Information" bordered column={2}>
          <Descriptions.Item label="Order Number">
            {order.order_number}
          </Descriptions.Item>
          <Descriptions.Item label="Order Status">
            <Tag color={getStatusColor(order.status)}>{getStatusText(order.status)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Customer Name">
            {order.customer_name}
          </Descriptions.Item>
          <Descriptions.Item label="Customer Email">
            {order.customer_email || 'Not provided'}
          </Descriptions.Item>
          <Descriptions.Item label="Customer Phone">
            {order.customer_phone}
          </Descriptions.Item>
          <Descriptions.Item label="Order Amount">
            ${normalizeMoney(order.total_amount ?? '0')}
          </Descriptions.Item>
          <Descriptions.Item label="Channel">
            {order.channel || 'manual'}
          </Descriptions.Item>
          <Descriptions.Item label="Created Time">
            {new Date(order.created_at).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Country">
            {formatCountry(order.dest_country, 'Not provided')}
          </Descriptions.Item>
          <Descriptions.Item label="Shipping Address" span={2}>
            {order.shipping_address}
          </Descriptions.Item>
          {order.message && (
            <Descriptions.Item label="Order Message" span={2}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                  {order.message}
                </div>
                {order.status === 'AWAITING_PAYMENT' && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Button
                      size="small"
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={handleEditMessage}
                      style={{ backgroundColor: '#ff8c00', borderColor: '#ff8c00' }}
                    >
                      Edit
                    </Button>
                    <Text style={{ fontSize: 11, color: '#666', marginTop: 4, textAlign: 'right' }}>
                      You can edit the order message until the order has been paid
                    </Text>
                  </div>
                )}
              </div>
            </Descriptions.Item>
          )}
          {!order.message && order.status === 'AWAITING_PAYMENT' && (
            <Descriptions.Item label="Order Message" span={2}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text style={{ color: '#999', fontStyle: 'italic' }}>No message provided</Text>
                <Button
                  size="small"
                  type="dashed"
                  icon={<EditOutlined />}
                  onClick={handleEditMessage}
                >
                  Add Message
                </Button>
                <Text style={{ fontSize: 11, color: '#666' }}>
                  You can edit the order message until the order has been paid
                </Text>
              </div>
            </Descriptions.Item>
          )}
          {order.third_party_info && (
            <Descriptions.Item label="Third-Party Info" span={2}>
              <div style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 4 }}>
                {Object.entries(order.third_party_info).map(([key, value]) => (
                  <div key={key} style={{ marginBottom: 8 }}>
                    <Text strong style={{ color: BRAND_PRIMARY }}>{humanizeKey(key)}:</Text>
                    <div style={{ marginLeft: 8, marginTop: 2 }}>
                      {String(key).toLowerCase() === 'url' ? (
                        <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_PRIMARY }}>
                          {value}
                        </a>
                      ) : (
                        <Text>{value}</Text>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Descriptions.Item>
          )}
        </Descriptions>

        {order.items_detail && order.items_detail.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <h3>Order Items</h3>
            <List
              dataSource={order.items_detail}
              renderItem={(item, index) => {
                const product = products[item.product_id];
                const coverImageUrl = product ? getProductCoverImage(product) : null;
                const variantImageUrl = product ? getVariantImage(product, item.variant_path) : null;
                const freightChannel = item.freight_id ? freightChannelMap[item.freight_id] : null;
                const customization = item.customization_id ? customizationMap[item.customization_id] : null;

                const handleImageError = (e) => {
                  if (variantImageUrl && e.target.src === variantImageUrl && coverImageUrl) {
                    e.target.src = coverImageUrl;
                    e.target.style.border = 'none';
                  } else {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                    e.target.style.border = 'none';
                  }
                };

                return (
                  <List.Item key={index}>
                    <Card size="small" style={{ width: '100%' }}>
                      <Row gutter={16}>
                        <Col span={4}>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Image
                              src={variantImageUrl || coverImageUrl}
                              alt={variantImageUrl ? 'Variant image' : 'Product cover'}
                              width={60}
                              height={60}
                              style={{
                                objectFit: 'cover',
                                borderRadius: 4,
                                border: variantImageUrl ? `2px solid ${BRAND_PRIMARY}` : 'none',
                                cursor: 'pointer',
                              }}
                              onError={handleImageError}
                            />
                          </div>
                        </Col>
                        <Col span={8}>
                          <div
                            style={{
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              color: BRAND_PRIMARY,
                              marginBottom: 4,
                            }}
                            onClick={() => handleProductClick(item.product_id)}
                            onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                            onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                          >
                            {product ? product.name : `Product ID: ${item.product_id}`}
                          </div>
                          {item.variant_path && item.variant_path.length > 0 && (
                            <div style={{ fontSize: 12, color: '#666' }}>
                              Variant: {item.variant_path.join(' - ')}
                            </div>
                          )}
                          {item.sku && (
                            <div style={{ fontSize: 12, color: '#666' }}>
                              SKU: {item.sku}
                            </div>
                          )}
                          <div>
                            Quantity: {item.quantity} | Unit Price: ${normalizeMoney(item.unit_price ?? '0')}
                          </div>
                          <div>Weight: {item.weight}g × {item.quantity} = {item.total_weight}g</div>
                          {item.freight_id && (
                            <div style={{ fontSize: 12, color: '#666' }}>
                              Freight Channel: <Text strong>{freightChannel?.name || `ID: ${item.freight_id}`}</Text>
                            </div>
                          )}
                          {item.customization_id && (
                            <div style={{ fontSize: 12, color: '#666' }}>
                              Customization: <Text strong>{customization?.customization_name || `ID: ${item.customization_id}`}</Text>
                            </div>
                          )}
                        </Col>
                        <Col span={6}>
                          <div>Fulfillment Status:</div>
                          <Tag color={getFulfillmentStatusColor(item.fulfillment_status)}>
                            {getFulfillmentStatusText(item.fulfillment_status)}
                          </Tag>
                          {item.tracking_number && (
                            <div style={{ fontSize: 12, marginTop: 4 }}>
                              Tracking: {item.tracking_number}
                            </div>
                          )}
                          {item.tracking_url && (
                            <div style={{ fontSize: 12, marginTop: 4 }}>
                              Tracking URL:{' '}
                              <a href={item.tracking_url} target="_blank" rel="noopener noreferrer">
                                {item.tracking_url}
                              </a>
                            </div>
                          )}
                          {item.comment && (
                            <div style={{ fontSize: 12, marginTop: 4, color: '#666' }}>
                              Comment: {item.comment}
                            </div>
                          )}
                        </Col>
                        <Col span={6}>
                          {item.rma_status && (
                            <>
                              <div>RMA Status:</div>
                              <Tag color={getRMAStatusColor(item.rma_status)}>
                                {getRMAStatusText(item.rma_status)}
                              </Tag>
                              {item.rma_shipment && (
                                <div style={{ fontSize: 12, marginTop: 4 }}>
                                  RMA Shipment: {item.rma_shipment}
                                </div>
                              )}
                              {item.rma_comment && (
                                <div style={{ fontSize: 12, marginTop: 4, color: '#666' }}>
                                  RMA Comment: {item.rma_comment}
                                </div>
                              )}
                            </>
                          )}
                        </Col>
                      </Row>
                    </Card>
                  </List.Item>
                );
              }}
            />
          </div>
        )}

        {order.price_details && order.price_details.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <h3>Price Details</h3>
            <Table
              dataSource={order.price_details}
              rowKey="name"
              pagination={false}
              size="small"
              showHeader={false}
              columns={[
                {
                  title: 'Item',
                  dataIndex: 'name',
                  key: 'name',
                  width: '60%',
                  render: (name) => <strong>{name}</strong>,
                },
                {
                  title: 'Amount',
                  key: 'amount',
                  width: '40%',
                  render: (_, record) => (
                    <div style={{ textAlign: 'right' }}>
                      <strong>${normalizeMoney(record.amount ?? '0')} {record.unit}</strong>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
      </div>
    );
  }, [order, products, freightChannelMap, customizationMap]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return (
      <div>
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <p>Order not found.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <div style={{ fontSize: 20, fontWeight: 'bold' }}>Order Details</div>
      </div>
      {orderContent}

      <Modal
        title="Edit Order Message"
        open={messageModalVisible}
        onCancel={() => setMessageModalVisible(false)}
        onOk={() => messageForm.submit()}
        okText="Update Message"
        okButtonProps={{
          style: { backgroundColor: '#ff8c00', borderColor: '#ff8c00' },
          loading: updatingMessage,
        }}
        cancelText="Cancel"
        width={600}
      >
        <Form form={messageForm} layout="vertical" onFinish={handleUpdateMessage}>
          <Form.Item label="Order Message" name="message">
            <TextArea
              placeholder="Enter any special instructions or notes for this order..."
              rows={4}
              maxLength={1000}
              showCount
            />
          </Form.Item>
          <div
            style={{
              padding: '8px 12px',
              backgroundColor: '#f0f8ff',
              borderRadius: 4,
              fontSize: 12,
              color: '#666',
            }}
          >
            <strong>Note:</strong> You can edit the order message until the order has been paid.
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderDetails;
